import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Skeleton,
  Pagination,
  Button,
  Divider,
  IconButton,
  // Rating,
} from '@mui/material';
import {
  Search,
  LocationOn,
  Business as BusinessIcon,
  Verified,
  // Visibility,
  Phone,
  WhatsApp,
  // Email,
  Star,
  ShareOutlined,
  Add,
} from '@mui/icons-material';
import { Business, BUSINESS_CATEGORIES } from '@/constants/types';
import { businessService } from '@/services/business/businessService';
import { useNavigate } from 'react-router-dom';
import { getFileUrl } from '@/utils/businessHelpers';
import { ShareBusinessModal } from './ShareBusinessModal';

const CardSkeleton: React.FC = () => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
    <Skeleton variant="rectangular" height={180} />
    <CardContent>
      <Skeleton width="60%" height={24} sx={{ mb: 1 }} />
      <Skeleton width="40%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton width="50%" height={20} />
    </CardContent>
  </Card>
);

export const AllBusinesses: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [shareBusiness, setShareBusiness] = useState<Business | null>(null);

  useEffect(() => {
    loadBusinesses();
  }, [globalFilter, categoryFilter, cityFilter, stateFilter, page]);

  useEffect(() => {
    setPage(1);
  }, [globalFilter, categoryFilter, cityFilter, stateFilter]);

  const loadBusinesses = async () => {
    try {
      setIsLoading(true);
      const params: any = {
        page,
        limit: pageSize,
        sort: '-createdAt',
        isVerified: true,
        approvalStatus: 'approved',
      };

      if (globalFilter) params.search = globalFilter;
      if (categoryFilter !== 'all') params.businessCategory = categoryFilter;
      if (cityFilter) params.city = cityFilter;
      if (stateFilter) params.state = stateFilter;

      const response = await businessService.getAllBusinesses(params);
      setBusinesses(response.data.businesses);
      setPagination(response.data.pagination);
    } catch (err: any) {
      console.error('Failed to load businesses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (business: Business) => {
    navigate(`/businesses/${business._id}`);
  };

  const totalPages = pagination ? Math.ceil(pagination.totalItems / pageSize) : 1;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          Explore Businesses
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/my-business/new')}
          disabled={isLoading}
        >
          Add Business
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 12 }}>
            <TextField
              fullWidth
              placeholder="Search businesses..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              select
              size="small"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {BUSINESS_CATEGORIES.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="City"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              placeholder="Enter city"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="State"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              placeholder="Enter state"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Business Cards */}
      {isLoading ? (
        <Grid container spacing={3}>
          {Array.from({ length: pageSize }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : businesses.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <BusinessIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No businesses found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Try adjusting your filters
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {businesses.map((business) => (
            <Grid key={business._id} size={{ xs: 12 }}>
              <Card
                elevation={0}
                onClick={() => handleViewDetails(business)}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: theme.shadows[4] },
                }}
              >
                {/* Top: Banner with Logo */}
                <Box sx={{ position: 'relative' }}>
                  {/* Banner */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: { xs: 120, sm: 150, md: 180 },
                      overflow: 'hidden',
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                    }}
                  >
                    {getFileUrl(business.bannerId) ? (
                      <Box
                        component="img"
                        src={getFileUrl(business.bannerId)}
                        alt={`${business.businessName} Banner`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}22 0%, ${theme.palette.primary.main}44 100%)`,
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: { xs: 40, md: 60 }, color: 'text.disabled' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Logo - Overlapping */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -45,
                      left: 16,
                      width: 90,
                      height: 90,
                      borderRadius: '50%',
                      border: `4px solid ${theme.palette.background.paper}`,
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'white',
                      overflow: 'hidden',
                      boxShadow: 2,
                    }}
                  >
                    {getFileUrl(business.logoId) ? (
                      <CardMedia component="img" image={getFileUrl(business.logoId)} alt={business.businessName} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BusinessIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Verified Badge */}
                  {business.isVerified && (
                    <Chip icon={<Verified sx={{ fontSize: 14 }} />} label="Verified" size="small" color="success" sx={{ position: 'absolute', top: 8, left: 8, height: 22, fontSize: '0.7rem' }} />
                  )}
                </Box>

                {/* Content */}
                <CardContent sx={{ pt: 6.5, pb: 1.5 }}>
                  {/* Row 1: Name */}
                  <Typography variant="h6" fontWeight={700} noWrap sx={{ mb: 0.5 }}>
                    {business.businessName}
                  </Typography>

                  {/* Row 2: Rating, Categories */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                   {false && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, bgcolor: 'success.main', color: 'white', px: 0.8, py: 0.2, borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ color: 'white' }}>{business.rating?.toFixed(1) || '0.0'}</Typography>
                      <Star sx={{ fontSize: 12 }} />
                    </Box>}
                   {false && <Typography variant="caption" color="text.secondary">({business.ratingsCount || 0})</Typography>}
                    {Array.isArray(business.businessCategory)
                      ? business.businessCategory.slice(0, 3).map((cat) => <Chip key={cat} label={cat} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />)
                      : <Chip label={business.businessCategory} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                  </Box>

                  {/* Services Offered */}
                  {business.businessServices && (Array.isArray(business.businessServices) ? business.businessServices.length > 0 : !!business.businessServices) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      {(() => {
                        const services = Array.isArray(business.businessServices)
                          ? business.businessServices
                          : [business.businessServices];
                        return (
                          <>
                            {services.slice(0, 3).map((svc) => (
                              <Chip
                                key={svc}
                                label={svc}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            ))}
                            {services.length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                +{services.length - 3} more
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Row 3: Location & Owner */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">{business.city}, {business.state} - {business.pincode}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.disabled">Owner: {business.ownerName}</Typography>
                  </Box>

                  {/* Row 4: Description (1 line with ellipsis) */}
                  {business.description && (
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {business.description}
                    </Typography>
                  )}
                </CardContent>

                {/* Action Buttons */}
                <Divider />
                <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button variant="contained" startIcon={<Phone />} size="small" href={`tel:${business.contactNumber}`} onClick={(e) => e.stopPropagation()} sx={{ textTransform: 'none', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>
                    {business.contactNumber}
                  </Button>
                  {business.whatsappNumber && (
                    <Button variant="outlined" startIcon={<WhatsApp />} size="small" href={`https://wa.me/91${business.whatsappNumber}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} sx={{ textTransform: 'none', borderColor: 'success.main', color: 'success.main', '&:hover': { borderColor: 'success.dark' } }}>
                      WhatsApp
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareBusiness(business);
                    }}
                    sx={{ ml: 'auto', border: '1px solid', borderColor: 'divider' }}
                  >
                    <ShareOutlined fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Share Business Modal */}
      <ShareBusinessModal
        open={!!shareBusiness}
        onClose={() => setShareBusiness(null)}
        business={shareBusiness}
      />
    </Box>
  );
};
